import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { ArrowLeft, Users, CalendarBlank, Buildings, HandHeart } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import HelpAlmaMaterDialog from "@/components/HelpAlmaMaterDialog";
import ImpactCertificate from "@/components/ImpactCertificate";
import ProjectDiscussion from "@/components/ProjectDiscussion";
import EmbedTickerDialog from "@/components/EmbedTickerDialog";
import { useAuth } from "@/context/AuthContext";
import { WhatsappLogo, Megaphone, Star } from "@phosphor-icons/react";

const STATUS_META = {
  active: "🟢 Active", urgent: "🔴 Urgent", partially_funded: "🟡 Partially Funded",
  fully_funded: "🔵 Fully Funded", completed: "⚪ Completed",
};

export default function ProjectDetail() {
  const { pid } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [imp, setImp] = useState({ impact_before: "", impact_after: "" });
  const [ty, setTy] = useState({ from: "", role: "", message: "" });
  const [photos, setPhotos] = useState({ before: "", after: "" });
  const [matchInfo, setMatchInfo] = useState(null);
  const [pubText, setPubText] = useState("");
  const load = useCallback(() => {
    api.get(`/projects/${pid}`).then((r) => { setP(r.data); setStatus(r.data.status); setImp({ impact_before: r.data.impact_before || "", impact_after: r.data.impact_after || "" }); }).catch(() => setP(false));
  }, [pid]);
  useEffect(() => { load(); }, [load]);
  useSeo(p && p !== false ? p.title : "Project", p && p !== false ? p.description : null);

  if (p === false) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-muted-foreground">Project not found.</div>;
  if (!p) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-muted-foreground">Loading…</div>;

  const saveStatus = async () => {
    try { await api.put(`/projects/${pid}`, { status, progress_note: note || undefined }); toast.success("Project updated"); setNote(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const saveImpact = async () => {
    const payload = { ...imp };
    if (photos.before.trim()) payload.before_photos = photos.before.split(/\s|,/).filter(Boolean);
    if (photos.after.trim()) payload.after_photos = photos.after.split(/\s|,/).filter(Boolean);
    try { await api.put(`/projects/${pid}`, payload); toast.success("Impact saved"); setPhotos({ before: "", after: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const addThankYou = async () => {
    if (!ty.message.trim()) { toast.error("Add a message"); return; }
    const list = [...(p.thank_you_messages || []), ty];
    try { await api.put(`/projects/${pid}`, { thank_you_messages: list }); toast.success("Thank-you added"); setTy({ from: "", role: "", message: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const notifyMatches = async () => {
    try { const r = await api.post(`/projects/${pid}/notify-matches`); toast.success(`Notified ${r.data.notified} matching alumni`); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const previewMatches = async () => {
    try { const r = await api.get(`/projects/${pid}/skill-matches`); setMatchInfo(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const volunteerChampion = async () => {
    try { const r = await api.post(`/projects/${pid}/champion`); toast.success(r.data.status === "active" ? "You're now a champion!" : "Request sent — the institution will approve you."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const approveChampion = async (uid) => { try { await api.post(`/projects/${pid}/champions/${uid}/approve`); toast.success("Champion approved"); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const removeChampion = async (uid) => { try { await api.delete(`/projects/${pid}/champions/${uid}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const publishToGroup = async () => {
    if (!pubText.trim()) { toast.error("Write something to publish"); return; }
    try { await api.post(`/projects/${pid}/publish-to-group`, { text: pubText }); toast.success("Published to the alumni group"); setPubText(""); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const shareWhatsApp = () => {
    const origin = window.location.origin;
    const msg = `${p.institution?.name || "Our institution"} needs help: ${p.title}\n${p.description || ""}\n\nSee & help on AlumniPulse: ${origin}/projects/${pid}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const postToGroupWhatsApp = async () => {
    const origin = window.location.origin;
    const text = (pubText.trim() || `Update on "${p.title}" — see & help: ${origin}/projects/${pid}`);
    try { await navigator.clipboard.writeText(text); toast.success("Update copied — paste it into the group chat"); } catch { /* clipboard may be blocked */ }
    window.open(p.group_whatsapp_url, "_blank");
  };
  const mc = p.my_contribution;
  const certEligible = mc && ["verified", "completed"].includes(mc.status);
  const canEdit = p.can_manage || p.can_champion;
  const cur = (p.institution && (p.institution.country === "India" || p.institution.country === "india")) ? "₹" : "$";

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" data-testid="project-detail">
      <Link to={p.institution ? `/institutions/${p.institution.id}` : "/explore/institutions"} className="mb-4 inline-flex items-center gap-1.5 text-sm text-secondary hover:underline">
        <ArrowLeft size={15} /> {p.institution ? p.institution.name : "Back"}
      </Link>

      <div className="rounded-2xl border border-border/60 bg-white p-6">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="rounded-full">{p.category}</Badge>
          {p.subcategory && <Badge variant="outline" className="rounded-full">{p.subcategory}</Badge>}
          <span>{STATUS_META[p.status] || p.status}</span>
          {p.verification === "institution_verified" && <Badge className="rounded-full bg-secondary/15 text-secondary">Institution Verified</Badge>}
        </div>
        <h1 className="mt-2 font-heading text-3xl font-medium">{p.title}</h1>
        {p.institution && <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><Buildings size={15} /> {p.institution.name}{p.institution.location ? ` · ${p.institution.location}` : ""}</div>}

        <div className="mt-3"><EmbedTickerDialog projectId={p.id} /></div>

        {(p.target_year || p.target_department) && (
          <div className="mt-3 rounded-lg border border-secondary/30 bg-secondary/5 px-3 py-2 text-sm text-secondary" data-testid="target-banner">
            🎯 Calling {p.target_year && <strong>{p.target_year}</strong>}{p.target_year && p.target_department ? " " : ""}{p.target_department && <strong>{p.target_department}</strong>} alumni — your alma mater needs you.
          </div>
        )}

        {p.funding_goal > 0 && (
          <div className="mt-5 rounded-xl bg-muted/30 p-4">
            <Progress value={p.funded_pct} className="h-3" />
            <div className="mt-2 flex justify-between text-sm">
              <span className="font-medium">{cur}{(p.amount_pledged || 0).toLocaleString()} pledged</span>
              <span className="text-muted-foreground">{p.funded_pct}% of {cur}{p.funding_goal.toLocaleString()} · {cur}{(p.amount_remaining || 0).toLocaleString()} to go</span>
            </div>
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { icon: Users, label: "Students helped", value: p.students_affected || "—" },
            { icon: HandHeart, label: "Supporters", value: p.supporter_count || 0 },
            { icon: CalendarBlank, label: "Deadline", value: p.deadline || "—" },
            { icon: CalendarBlank, label: "Target", value: p.target_date || "—" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border/60 p-3">
              <s.icon className="text-secondary" size={18} weight="duotone" />
              <div className="mt-1 font-medium">{s.value}</div>
              <div className="text-xs text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>

        {p.description && <p className="mt-5 leading-relaxed text-foreground/90">{p.description}</p>}
        {p.why_needed && <div className="mt-4"><div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Why it's needed</div><p className="mt-1 text-sm text-foreground/90">{p.why_needed}</p></div>}
        {p.who_benefits && <div className="mt-3"><div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Who benefits</div><p className="mt-1 text-sm text-foreground/90">{p.who_benefits}</p></div>}

        {(p.impact_before || p.impact_after || p.before_photos?.length || p.after_photos?.length) && (
          <div className="mt-6" data-testid="see-the-impact">
            <h2 className="font-heading text-xl font-medium">You helped build this</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-border/60 p-4">
                <div className="text-xs font-semibold uppercase text-muted-foreground">Before</div>
                {p.impact_before && <p className="mt-1 text-sm">{p.impact_before}</p>}
                <div className="mt-2 grid grid-cols-2 gap-2">{(p.before_photos || []).map((src, i) => <img key={i} src={src} alt="before" className="h-24 w-full rounded-lg object-cover" />)}</div>
              </div>
              <div className="rounded-xl border border-secondary/40 bg-secondary/5 p-4">
                <div className="text-xs font-semibold uppercase text-secondary">After</div>
                {p.impact_after && <p className="mt-1 text-sm">{p.impact_after}</p>}
                <div className="mt-2 grid grid-cols-2 gap-2">{(p.after_photos || []).map((src, i) => <img key={i} src={src} alt="after" className="h-24 w-full rounded-lg object-cover" />)}</div>
              </div>
            </div>
          </div>
        )}

        {p.thank_you_messages?.length > 0 && (
          <div className="mt-5" data-testid="thank-you-messages">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Thank-you from the institution</div>
            <div className="mt-2 space-y-2">
              {p.thank_you_messages.map((t, i) => (
                <div key={i} className="rounded-xl border border-border/60 bg-white p-3">
                  <p className="text-sm italic">"{t.message}"</p>
                  {(t.from || t.role) && <div className="mt-1 text-xs text-muted-foreground">— {t.from}{t.role ? `, ${t.role}` : ""}</div>}
                </div>
              ))}
            </div>
          </div>
        )}


        {(p.impact_badges || []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {p.impact_badges.map((b) => <span key={b} className="rounded-full bg-secondary/10 px-2.5 py-1 text-xs text-secondary">{b}</span>)}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {p.institution && <HelpAlmaMaterDialog instId={p.institution.id} instName={p.institution.name} projectId={p.id} projectTitle={p.title} label="I want to help with this" size="lg" onDone={load} />}
          {certEligible && <ImpactCertificate contribution={mc} projectTitle={p.title} institutionName={p.institution?.name} />}
          <Button variant="outline" className="gap-1.5 border-green-500/40 text-green-700 hover:bg-green-50" onClick={shareWhatsApp} data-testid="share-whatsapp-btn"><WhatsappLogo size={18} weight="fill" /> Share to WhatsApp</Button>
        </div>
        {mc && !certEligible && <p className="mt-2 text-xs text-muted-foreground">You've offered to help — your certificate unlocks once the institution verifies it.</p>}
        <p className="mt-3 text-xs text-muted-foreground">AlumniPulse is a facilitator only — we never handle or transfer funds. Any support is arranged directly with the institution.</p>

        {/* Champions */}
        <div className="mt-6 rounded-xl border border-border/60 p-4" data-testid="champions-section">
          <div className="flex items-center gap-2"><Star size={18} weight="duotone" className="text-secondary" /><h3 className="font-medium">Project champions</h3></div>
          <p className="text-xs text-muted-foreground">Alumni who promote this need, post progress and rally the community.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {(p.champions || []).filter((c) => c.status === "active").map((c) => (
              <span key={c.user_id} className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2.5 py-1 text-xs text-secondary" data-testid={`champion-${c.user_id}`}>
                <Star size={12} weight="fill" /> {c.name}
                {p.can_manage && <button className="ml-1 text-muted-foreground hover:text-red-600" onClick={() => removeChampion(c.user_id)}>×</button>}
              </span>
            ))}
            {(p.champions || []).filter((c) => c.status === "active").length === 0 && <span className="text-xs text-muted-foreground">No champions yet.</span>}
          </div>
          {p.can_manage && (p.champions || []).some((c) => c.status === "pending") && (
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase text-muted-foreground">Pending requests</div>
              {(p.champions || []).filter((c) => c.status === "pending").map((c) => (
                <div key={c.user_id} className="mt-1 flex items-center gap-2 text-sm">
                  <span>{c.name}</span>
                  <Button size="sm" className="h-6" onClick={() => approveChampion(c.user_id)} data-testid={`approve-champion-${c.user_id}`}>Approve</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-red-600" onClick={() => removeChampion(c.user_id)}>Decline</Button>
                </div>
              ))}
            </div>
          )}
          {user && !p.am_champion && (
            <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={volunteerChampion} data-testid="volunteer-champion-btn"><Star size={14} /> Volunteer to champion this</Button>
          )}
          {p.am_champion && !p.can_manage && <p className="mt-2 text-xs text-secondary">You're a champion of this project 🌟 · <Link to="/champion" className="underline" data-testid="champion-dashboard-link">Open your Champion Dashboard</Link></p>}
        </div>



        {p.supporters?.length > 0 && (
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Alumni supporters</div>
            <div className="mt-2 space-y-1.5">
              {p.supporters.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-sm"><HandHeart size={14} className="text-secondary" /> <span className="font-medium">{s.name}</span> <span className="text-muted-foreground">· {s.help_label}</span></div>
              ))}
            </div>
          </div>
        )}

        {p.progress_updates?.length > 0 && (
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Progress updates</div>
            <div className="mt-2 space-y-2">
              {p.progress_updates.map((u, i) => (
                <div key={i} className="rounded-lg border border-border/60 p-3 text-sm"><p>{u.note}</p><div className="mt-1 text-xs text-muted-foreground">{u.by} · {(u.at || "").slice(0, 10)}</div></div>
              ))}
            </div>
          </div>
        )}

        {canEdit && (
          <div className="mt-6 rounded-xl border border-dashed border-secondary/40 p-4" data-testid="project-manage">
            <div className="text-sm font-medium">{p.can_manage ? "Manage this project" : "Champion tools"}</div>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s]}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Add a progress note (auto-posts to the alumni group)</Label><Textarea rows={1} value={note} onChange={(e) => setNote(e.target.value)} data-testid="project-progress-note" /></div>
            </div>
            <Button size="sm" className="mt-3" onClick={saveStatus} data-testid="project-save-status">Save</Button>

            <div className="mt-4 border-t border-border/50 pt-4">
              <div className="text-sm font-medium flex items-center gap-1.5"><Megaphone size={15} /> Publish an update to the alumni group</div>
              <p className="text-xs text-muted-foreground">Posts to the institution's alumni group discussion feed.</p>
              <div className="mt-2 flex gap-2">
                <Textarea rows={1} value={pubText} onChange={(e) => setPubText(e.target.value)} placeholder="e.g. We're halfway there — 500 of 1000 books collected!" data-testid="publish-group-text" />
                <Button size="sm" onClick={publishToGroup} data-testid="publish-group-btn">Publish</Button>
              </div>
              {p.group_whatsapp_url && (
                <Button size="sm" variant="outline" className="mt-2 gap-1.5 border-green-500/40 text-green-700 hover:bg-green-50" onClick={postToGroupWhatsApp} data-testid="wa-deeppost-btn">
                  <WhatsappLogo size={16} weight="fill" /> Post to the group's WhatsApp
                </Button>
              )}
            </div>

            {p.can_manage && (
            <div className="mt-4 border-t border-border/50 pt-4">
              <div className="text-sm font-medium">Skills matching</div>
              <p className="text-xs text-muted-foreground">Notify alumni of this institution whose skills match this need.</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={previewMatches} data-testid="preview-matches-btn">Preview matches</Button>
                <Button size="sm" onClick={notifyMatches} data-testid="notify-matches-btn">Notify matching alumni</Button>
                {matchInfo && <span className="text-xs text-muted-foreground">{matchInfo.count} match{matchInfo.count === 1 ? "" : "es"} found{matchInfo.already_notified ? ` · ${matchInfo.already_notified} notified` : ""}</span>}
              </div>
            </div>
            )}

            <div className="mt-4 border-t border-border/50 pt-4">
              <div className="text-sm font-medium">See the Impact (before / after)</div>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Before (text)</Label><Textarea rows={2} value={imp.impact_before} onChange={(e) => setImp({ ...imp, impact_before: e.target.value })} data-testid="impact-before" /></div>
                <div className="space-y-1.5"><Label>After (text)</Label><Textarea rows={2} value={imp.impact_after} onChange={(e) => setImp({ ...imp, impact_after: e.target.value })} data-testid="impact-after" /></div>
                <div className="space-y-1.5"><Label>Before photo URLs (space/comma separated)</Label><Input value={photos.before} onChange={(e) => setPhotos({ ...photos, before: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>After photo URLs</Label><Input value={photos.after} onChange={(e) => setPhotos({ ...photos, after: e.target.value })} /></div>
              </div>
              <Button size="sm" className="mt-2" onClick={saveImpact} data-testid="save-impact-btn">Save impact</Button>
            </div>

            <div className="mt-4 border-t border-border/50 pt-4">
              <div className="text-sm font-medium">Add a thank-you message</div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <Input placeholder="From (e.g. Priya, Grade 11)" value={ty.from} onChange={(e) => setTy({ ...ty, from: e.target.value })} />
                <Input placeholder="Role (e.g. Student, Principal)" value={ty.role} onChange={(e) => setTy({ ...ty, role: e.target.value })} />
              </div>
              <Textarea className="mt-2" rows={2} placeholder="Message" value={ty.message} onChange={(e) => setTy({ ...ty, message: e.target.value })} data-testid="thankyou-message" />
              <Button size="sm" className="mt-2" onClick={addThankYou} data-testid="add-thankyou-btn">Add thank-you</Button>
            </div>
          </div>
        )}
      </div>

      <ProjectDiscussion pid={pid} canModerate={canEdit} />
    </div>
  );
}
